import React, { useMemo, useState } from "react";
import { Check, ChevronsUpDown, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";

/**
 * Multi-select dropdown with search.
 *
 * Props
 * - placeholder: string shown when no items selected
 * - options:     [{ id, name }]
 * - value:       string[] of selected ids
 * - onChange:    (string[]) => void
 * - className:   trigger className
 * - testId:      data-testid for the trigger
 */
export const MultiSelect = ({
  placeholder = "Select…",
  options = [],
  value = [],
  onChange,
  className,
  testId,
}) => {
  const [open, setOpen] = useState(false);

  const valueSet = useMemo(() => new Set(value), [value]);

  const toggle = (id) => {
    const next = valueSet.has(id)
      ? value.filter((v) => v !== id)
      : [...value, id];
    onChange(next);
  };

  const clearAll = (e) => {
    e.stopPropagation();
    onChange([]);
  };

  const selectedNames = options
    .filter((o) => valueSet.has(o.id))
    .map((o) => o.name);

  const labelNode = (() => {
    if (selectedNames.length === 0) {
      return <span className="text-muted-foreground">{placeholder}</span>;
    }
    if (selectedNames.length === 1) {
      return (
        <span className="truncate" title={selectedNames[0]}>
          {selectedNames[0]}
        </span>
      );
    }
    return (
      <span className="truncate" title={selectedNames.join(", ")}>
        {selectedNames[0]}
        <span className="ml-1 inline-flex items-center justify-center text-[10px] font-semibold rounded-full bg-[#1F3A5F] text-white px-1.5 py-0.5 leading-none align-middle">
          +{selectedNames.length - 1}
        </span>
      </span>
    );
  })();

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          data-testid={testId}
          className={cn(
            "flex h-9 items-center justify-between gap-1 rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors hover:bg-secondary/40 focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50",
            className
          )}
        >
          <span className="flex-1 text-left truncate">{labelNode}</span>
          {value.length > 0 ? (
            <span
              role="button"
              tabIndex={-1}
              onClick={clearAll}
              onPointerDown={(e) => e.stopPropagation()}
              className="opacity-60 hover:opacity-100"
              data-testid={testId ? `${testId}-clear` : undefined}
            >
              <X className="w-3.5 h-3.5" />
            </span>
          ) : (
            <ChevronsUpDown className="w-3.5 h-3.5 opacity-50 shrink-0" />
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[240px] p-0"
        align="start"
        sideOffset={4}
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <Command>
          <CommandInput placeholder={`Search ${placeholder.toLowerCase()}…`} />
          <CommandList>
            <CommandEmpty>No matches found.</CommandEmpty>
            <CommandGroup>
              {options.map((opt) => {
                const checked = valueSet.has(opt.id);
                return (
                  <CommandItem
                    key={opt.id}
                    value={`${opt.name} ${opt.id}`}
                    onSelect={() => toggle(opt.id)}
                    className="cursor-pointer"
                    data-testid={
                      testId ? `${testId}-option-${opt.id}` : undefined
                    }
                  >
                    <span
                      className={cn(
                        "mr-2 flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border border-primary",
                        checked
                          ? "bg-[#1F3A5F] border-[#1F3A5F] text-white"
                          : "opacity-50"
                      )}
                    >
                      {checked && <Check className="h-3 w-3" />}
                    </span>
                    <span className="truncate" title={opt.name}>
                      {opt.name}
                    </span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
